import React, { useContext } from 'react';
import PlayerAvatar, { playerDisplayName, playerFirstName } from './PlayerAvatar';
import LineupContext from '../../live/LineupContext';
import { getPairedPlayerId } from '../../live/utils/lineupUtils';

const idOf = (p) => p?.id ?? p?.userId;
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/** DOM key used to anchor the action popover to a player icon. */
export const playerAnchorKey = (participant, p) => `${participant}:${idOf(p) ?? playerDisplayName(p)}`;

/**
 * Shared inline player grid used by all sport scoreboards in advanced mode.
 * Circular avatar cards that wrap inside the card width. Tapping a card calls
 * onPlayerSelect(participant, player, buttonElement).
 *
 * Inside LiveMatch a LineupContext adds the bench strip and substitutions for
 * sports that allow them; without it every player is shown as on court.
 */
const InlinePlayerGrid = ({ participant, players = [], isDisabled = false, onPlayerSelect }) => {
  const ctx = useContext(LineupContext);

  if (players.length === 0) {
    return <span className="vb-adv-no-players">No players</span>;
  }

  const lineup = ctx?.getLineup?.(participant) || null;
  const byId = new Map(players.map((p) => [String(idOf(p)), p]));
  const pick = (ids) => ids.map((id) => byId.get(String(id))).filter(Boolean);
  const onCourt = lineup ? pick(lineup.onCourt) : players;
  const bench = lineup ? pick(lineup.bench) : [];

  const swap = ctx?.swap?.participant === participant ? ctx.swap : null;
  const canSwap = !!(lineup && ctx?.canSwap);
  const message = ctx?.message?.participant === participant ? ctx.message.text : null;
  const maxPerSet = ctx?.rules?.maxPerSet;

  const pairName = (p) => {
    if (!lineup) return null;
    const otherId = getPairedPlayerId(lineup, idOf(p));
    const other = otherId != null ? byId.get(String(otherId)) : null;
    return other ? playerFirstName(other) : null;
  };

  const cardBody = (p) => {
    const paired = pairName(p);
    return (
      <>
        <PlayerAvatar player={p} />
        <span className="vb-adv-player-mini-name">{playerDisplayName(p)}</span>
        {paired && <span className="vb-adv-pair-badge" title={`Paired with ${paired}`}>↔ {paired}</span>}
      </>
    );
  };

  const swapClass = (valid, selected) => [
    selected ? 'vb-adv-player-card-mini--selected' : '',
    valid === true ? 'vb-adv-player-card-mini--swap-target' : '',
    valid === false ? 'vb-adv-player-card-mini--swap-dim' : '',
  ].filter(Boolean).join(' ');

  const renderCourtCard = (p) => {
    const pid = idOf(p);
    const key = playerAnchorKey(participant, p);
    if (swap) {
      const selected = swap.outId != null && sameId(swap.outId, pid);
      const valid = swap.inId != null ? ctx.canSubstitute(participant, pid, swap.inId).allowed : null;
      return (
        <button
          key={key}
          type="button"
          className={`vb-adv-player-card-mini ${swapClass(valid, selected)}`.trim()}
          aria-disabled={valid === false || undefined}
          aria-pressed={selected}
          title={playerDisplayName(p)}
          data-swap-zone="true"
          onClick={() => ctx.onCourtSwapTap(participant, p)}
        >
          {cardBody(p)}
        </button>
      );
    }
    return (
      <button
        key={key}
        type="button"
        className="vb-adv-player-card-mini"
        disabled={isDisabled}
        title={playerDisplayName(p)}
        data-player-anchor={key}
        aria-haspopup="dialog"
        aria-expanded={ctx?.activeAnchorKey === key}
        onClick={(e) => !isDisabled && onPlayerSelect(participant, p, e.currentTarget)}
      >
        {cardBody(p)}
      </button>
    );
  };

  const renderBenchCard = (p) => {
    const pid = idOf(p);
    const selected = swap?.inId != null && sameId(swap.inId, pid);
    const valid = swap?.outId != null ? ctx.canSubstitute(participant, swap.outId, pid).allowed : null;
    return (
      <button
        key={`bench-${pid}`}
        type="button"
        className={`vb-adv-player-card-mini vb-adv-player-card-mini--bench ${swapClass(valid, selected)}`.trim()}
        disabled={!canSwap}
        aria-pressed={selected}
        aria-disabled={valid === false || undefined}
        title={`${playerDisplayName(p)} (bench)`}
        data-swap-zone="true"
        onClick={() => canSwap && ctx.onBenchTap(participant, p)}
      >
        {cardBody(p)}
      </button>
    );
  };

  let hint = null;
  if (swap?.inId != null) {
    hint = `Tap who comes off for ${playerFirstName(byId.get(String(swap.inId)))}`;
  } else if (swap?.outId != null) {
    hint = `Tap the bench player coming on for ${playerFirstName(byId.get(String(swap.outId)))}`;
  }

  return (
    <>
      <div className="vb-adv-player-grid">
        {onCourt.map(renderCourtCard)}
      </div>
      {(hint || message) && (
        <div className={`vb-adv-lineup-hint${message ? ' vb-adv-lineup-hint--warn' : ''}`} role="status">
          {message || hint}
        </div>
      )}
      {bench.length > 0 && (
        <div className="vb-adv-bench" aria-label="Bench">
          <div className="vb-adv-bench__header">
            <span className="vb-adv-bench__label">Bench</span>
            {maxPerSet != null && (
              <span className="vb-adv-bench__count">Subs {lineup.subsThisSet || 0}/{maxPerSet}</span>
            )}
          </div>
          <div className="vb-adv-bench__grid">
            {bench.map(renderBenchCard)}
          </div>
        </div>
      )}
    </>
  );
};

export default InlinePlayerGrid;
